/* global airbrake */

import React from 'react';
import connectToStores from 'alt-store-connect';
import EditorActions from '../../actions/editor';
import PageActions from '../../actions/page';
import PageStore from '../../stores/page';
import EditorStore from '../../stores/editor';
import Model from '../../utils/model';
import PageConfig from './page-config';
import PageEditorCanvas from './page-editor-canvas';
import PageConditionsWrapper from './page-conditions-wrapper';
import PageClone from './page-clone';
import MetadataEditor from '../metadata-editor';
import FlowSource from '../../sources/flow';
import Config from '../flow/config';
import DragContext from '../../utils/dragDropContext';
import Actionbar from '../actionbar';
import SharedSource from '../../sources/shared';

import { connect } from 'react-redux';
import { Components, UnknownComponent } from '../../config/components';
import { CSSTransitionGroup } from 'react-transition-group';

import '../../../css/page/page-editor.less';
import '../../../css/page/page-editor-config.less';

/**
 *
 * @param {String} pageId
 * @param {Object} model
 * @param {String} tenantId
 * @param {String} authenticationToken
 * @param {Function} initializeEmptyPage
 * @param {Function} openConfig
 * @description Determines whether a page exists. If it does, then the page gets rendered
 * otherwise a modal window is displayed for naming the page. Because the client-side state
 * is so convoluted for the page editor, the only accurate way to know whether it is being newly
 * created is to ask the engine.
 */
export const initializePageEditor = async (pageId, model, tenantId, authenticationToken, initializeEmptyPage, openConfig) => {
    const developerName = manywho.utils.getObjectDataProperty(model.properties, 'DeveloperName').contentValue;

    const pageExists = await SharedSource.get(pageId, 'page', tenantId, authenticationToken)
        .then(() => true)

        // The shared element build endpoint actually sends back a 400 status code
        // instead of a 404 if the page does not exist. So this catch block will
        // execute whenever something goes wrong with the API request.
        .catch(() => false);

    // The missing developerName occurs when creating a new page from a map element.
    // The flow makes a service call which actually saves the page
    // before the tab opens... but the runtime model doesn't update
    // so we can assume this must be a new page.
    if (pageExists && developerName) {
        PageActions.initializeModel(model, pageId);
    } else {

        // Since this is a new page with no containers we
        // want to generate some page container boilerplate metadata
        // so that the editor knows to render the main container
        initializeEmptyPage(model);
        PageActions.initializeModel(model, pageId);

        // And now open the modal so that the page name can be saved
        openConfig(true);
    }
};

@connectToStores
export class PageEditor extends React.Component {

    static getStores() {
        return [PageStore, EditorStore];
    }

    static getPropsFromStores(props) {
        const model = manywho.model.getComponent(props.id, props.flowKey).objectData[0];
        const pageId = manywho.utils.getObjectDataProperty(model.properties, 'Id').contentValue;
        return {
            page: PageStore.getState().models[pageId],
            flows: PageStore.getState().flows[pageId],
            flowContext: PageStore.getState().flowContext[pageId],
            editors: EditorStore.getState().editors,
        };
    }

    constructor(props) {
        super(props);

        this.state = { isMetadataVisible: false };
        this.savePage = this.savePage.bind(this);
        this.toggleMetadata = this.toggleMetadata.bind(this);
        this.editContainerDone = this.editContainerDone.bind(this);
        this.editComponentDone = this.editComponentDone.bind(this);
        this.editConditionsDone = this.editConditionsDone.bind(this);
        this.initializeEmptyPage = this.initializeEmptyPage.bind(this);
        this.openConfig = this.openConfig.bind(this);
        this.openConditions = this.openConditions.bind(this);
        this.openBasicConditions = this.openBasicConditions.bind(this);
        this.openClonePage = this.openClonePage.bind(this);
    }

    componentWillMount() {
        const model = manywho.model.getComponent(this.props.id, this.props.flowKey).objectData[0];
        const pageId = manywho.utils.getObjectDataProperty(model.properties, 'Id').contentValue;

        FlowSource.getFlows(this.props.tenantId, this.props.authenticationToken)
            .then((response) => {
                PageActions.setFlows(response, pageId);
            });

        initializePageEditor(
            pageId,
            model,
            this.props.tenantId,
            this.props.authenticationToken,
            this.initializeEmptyPage,
            this.openConfig,
        );
    }

    savePage() {
        const model = manywho.model.getComponent(this.props.id, this.props.flowKey);

        try {
            const pageId = manywho.utils.getObjectDataProperty(model.objectData[0].properties, 'Id').contentValue;

            const editorId = model.tags.filter(tag => manywho.utils.isEqual(tag.developerName, 'Editor Id'))[0].contentValue;

            EditorActions.setEditorNameAndType(editorId, this.props.page.model.developerName.value, 'page');
            PageActions.savePage(this.props.page.model.id.value, pageId, this.props.id, this.props.flowKey);

        } catch (ex) {
            airbrake.notify({
                error: ex,
                params: {
                    tenantId: this.props.tenantId,
                    tags: model ? model.tags : null,
                    page: this.props.page,
                },
            });
            throw ex;
        }
    }

    toggleMetadata() {
        this.setState({ isMetadataVisible: !this.state.isMetadataVisible });
    }

    editContainerDone(callback, response) {
        const command = manywho.utils.getObjectDataProperty(response.outputs, 'Command').contentValue;
        const pageContainer = manywho.utils.getObjectDataProperty(response.outputs, 'Page Container').objectData[0];

        const model = manywho.model.getComponent(this.props.id, this.props.flowKey).objectData[0];
        const pageId = manywho.utils.getObjectDataProperty(model.properties, 'Id').contentValue;

        if (command === 'Save') {
            PageActions.setContainer(pageContainer, pageId);
        } else if (command === 'Cancel') {

            if (this.props.page.editing.command === 'new') {
                PageActions.removeContainer(this.props.page.editing.id, pageId);
            }
        }

        PageActions.editContainerDone(pageId);
    }

    editComponentDone(callback, response) {
        const command = manywho.utils.getObjectDataProperty(response.outputs, 'Command').contentValue;
        const pageComponent = manywho.utils.getObjectDataProperty(response.outputs, 'Page Component').objectData[0];

        const model = manywho.model.getComponent(this.props.id, this.props.flowKey).objectData[0];
        const pageId = manywho.utils.getObjectDataProperty(model.properties, 'Id').contentValue;

        if (command === 'Save') {
            PageActions.setComponent(pageComponent, pageId);
        } else if (command === 'Cancel') {

            if (this.props.page.editing.command === 'new') {
                PageActions.removeComponent(this.props.page.editing.id, pageId);
            }
        }

        PageActions.editComponentDone(pageId);
    }

    editConditionsDone(callback, response) {
        if (response.outputs) {
            const command = manywho.utils.getObjectDataProperty(response.outputs, 'Command').contentValue;
            const pageId = manywho.utils.getObjectDataProperty(response.outputs, 'PageId').contentValue;

            if (command.toLowerCase() !== 'cancel') {
                const page = manywho.utils.getObjectDataProperty(response.outputs, 'Selected Page');
                const pageConditions = manywho.utils.getObjectDataProperty(page.objectData[0].properties, 'PageConditions');

                PageActions.setPageConditions(pageConditions.objectData, pageId);
            }

            PageActions.setEditingType(null, pageId);
        }
    }

    openConfig(isNew = false) {
        const model = manywho.model.getComponent(this.props.id, this.props.flowKey);
        const pageId = manywho.utils.getObjectDataProperty(model.objectData[0].properties, 'Id').contentValue;

        PageActions.setEditingType(isNew ? 'NEW_PAGE' : 'PAGE', pageId);
    }

    openConditions() {
        const model = manywho.model.getComponent(this.props.id, this.props.flowKey);
        const pageId = manywho.utils.getObjectDataProperty(model.objectData[0].properties, 'Id').contentValue;

        PageActions.setEditingType('CONDITIONS', pageId);
    }

    openBasicConditions() {
        const model = manywho.model.getComponent(this.props.id, this.props.flowKey);
        const pageId = manywho.utils.getObjectDataProperty(model.objectData[0].properties, 'Id').contentValue;

        PageActions.setEditingType('BASIC_CONDITIONS', pageId);
    }

    openClonePage() {
        const model = manywho.model.getComponent(this.props.id, this.props.flowKey);
        const pageId = manywho.utils.getObjectDataProperty(model.objectData[0].properties, 'Id').contentValue;

        PageActions.setEditingType('CLONE_PAGE', pageId);
    }

    initializeEmptyPage(model) {
        manywho.utils.getObjectDataProperty(model.properties, 'PageContainers').objectData = [];
        const containerId = Model.guid();

        const container = {
            developerName: 'PageContainer',
            typeElementId: 'a142f4a2-87e3-442b-b72e-23d102c3d90a',
            properties: [
                {
                    developerName: 'Id',
                    contentType: 'ContentString',
                    typeElementPropertyId: '50415c87-d6e6-49ff-bdb2-573a65d3ee34',
                    contentValue: containerId,
                },
                {
                    developerName: 'Order',
                    contentType: 'ContentNumber',
                    typeElementPropertyId: 'b26b7a52-ab7e-45ab-8a07-d020ccc98b2b',
                    contentValue: '0',
                },
                {
                    developerName: 'DeveloperName',
                    contentType: 'ContentString',
                    typeElementPropertyId: '4611be03-2153-44e3-921a-98dc7a2eab8b',
                    contentValue: 'Main Container',
                },
                {
                    developerName: 'ContainerType',
                    contentType: 'ContentString',
                    typeElementPropertyId: '01bc11a2-abdf-4038-a34f-8d41b56c5b13',
                    contentValue: 'VERTICAL_FLOW',
                },
                {
                    developerName: 'PageContainers',
                    contentType: 'ContentList',
                    typeElementPropertyId: 'c7733597-c67f-4b9b-a976-c00795521847',
                    objectData: null,
                },
            ],
        };

        manywho.utils.getObjectDataProperty(model.properties, 'PageContainers').objectData.push(container);
    }

    render() {
        const model = manywho.model.getComponent(this.props.id, this.props.flowKey);
        const pageId = manywho.utils.getObjectDataProperty(model.objectData[0].properties, 'Id').contentValue;
        const classes = manywho.styling.getClasses(this.props.parentId, this.props.id, 'page_builder', this.props.flowKey);
        const flowKey = manywho.utils.getFlowKey(pageId, 'page', 'page', 'page', 'page');

        if (this.props.page) {
            if (this.props.page.isPreview) {
                classes.push('preview');
            }

            let configElement = null;
            let inputs = [];
            const pageName = new URLSearchParams(window.location.search).get('page_name');
            const saveHiddenInputs = this.props.page.model.saveHiddenInputs.value || 'False';

            switch (this.props.page.editing.type) {
            case 'PAGE':
                configElement = (   
                    <PageConfig
                        page={this.props.page.model}
                        pageName={this.props.page.model.developerName.value}
                        saveHiddenInputs={saveHiddenInputs}
                        isNew={false}
                    />
                );
                break;

            case 'NEW_PAGE':
                configElement = (
                    <PageConfig
                        page={this.props.page.model}
                        pageName={pageName || ''}
                        saveHiddenInputs={saveHiddenInputs}
                        isNew
                        container={this}
                        closeTab={() => EditorActions.closePage(pageId)}
                    />
                );
                break;

            case 'BASIC_CONDITIONS':
                configElement = (
                    <PageConditionsWrapper
                        page={this.props.page.model}
                        tenantId={this.props.tenantId}
                        authenticationToken={this.props.authenticationToken}
                    />
                );

                break;

            case 'CONDITIONS':
                inputs.push({ PageId: pageId });
                inputs.push({ FlowId: this.props.flowContext || '' });
                inputs.push({ 'Selected Page': {
                    objectData: Model.toPageModel(this.props.page.model),
                    typeElementDeveloperName: 'PageElementRequest',
                },
                });

                configElement = (
                    <Config
                        manywhoTenantId={this.props.tenantId}
                        authenticationToken={this.props.authenticationToken}
                        flow="Page Conditions"
                        callbacks={this.editConditionsDone}
                        inputs={inputs}
                        key="page-conditions"
                        tenantId={manywho.settings.global('adminTenantId')}
                    />
                );

                break;

            case 'COMPONENT': {
                const callbacks = [{ execute: this.editComponentDone, type: 'done' }];

                if (this.props.page.editing.id && this.props.page.editing.componentType) {

                    let componentConfig = Components.filter(
                        component => manywho.utils.isEqual(component.id, this.props.page.editing.componentType, true),
                    )[0];

                    if (!componentConfig) {
                        componentConfig = UnknownComponent;
                    }

                    inputs = inputs.concat(Model.generatePageComponentInputs(componentConfig));

                    const component = Model.findComponent(this.props.page.editing.id, this.props.page.model.pageComponents.data);
                    if (component) {
                        const componentData = Model.convertToObjectDataProperties(component);
                        inputs.push({ 'Page Component': {
                            objectData: componentData,
                            typeElementDeveloperName: 'PageComponent',
                        },
                        });
                    } else {
                        inputs.push({ 'New Component Id': Model.guid() });
                        inputs.push({ 'New Component Order': this.props.page.droppedOrder });
                        inputs.push({ 'Component Type': this.props.page.editing.componentType });
                        inputs.push({ 'Page Component Parent': this.props.page.droppedOn });
                    }

                    inputs.push({ 'Page Component Value Type': componentConfig.bindingType || '' });
                    inputs.push({ 'Page Component Type Input': componentConfig.id.toUpperCase() || '' });
                    inputs.push({ 'Input TenantId': this.props.tenantId });
                    inputs.push({ 'Input AuthenticationToken': this.props.authenticationToken });
                    inputs.push({ FlowId: this.props.flowContext || '' });
                    inputs.push({ DocsUrl: componentConfig.docsUrl || '' });
                }

                configElement = (
                    <Config
                        manywhoTenantId={this.props.tenantId}
                        authenticationToken={this.props.authenticationToken}
                        callbacks={callbacks}
                        flow={this.props.page.editing.flow}
                        inputs={inputs}
                        key={this.props.page.editing.id}
                        tenantId={manywho.settings.global('adminTenantId')}
                    />
                );

                break;
            }

            case 'CONTAINER':
                if (this.props.page.editing.id) {
                    const objectData = Model.convertContainersToObjectData(
                        [Model.findContainer(this.props.page.editing.id,
                            this.props.page.model.pageContainers.data)],
                    )[0];
                    inputs.push({ 'Page Container': {
                        objectData,
                        typeElementDeveloperName: 'PageContainer',
                    } });
                }

                configElement = (
                    <Config
                        manywhoTenantId={this.props.tenantId}
                        authenticationToken={this.props.authenticationToken}
                        callbacks={this.editContainerDone}
                        flow={this.props.page.editing.flow}
                        inputs={inputs}
                        key={this.props.page.editing.id}
                        tenantId={manywho.settings.global('adminTenantId')}
                    />
                );

                break;

            case 'CLONE_PAGE':

                configElement = (<PageClone
                    manywhoTenantId={this.props.tenantId}
                    authenticationToken={this.props.authenticationToken}
                    page={this.props.page.model}
                    isDirty={this.props.page.isDirty}
                />
                );
                break;

            default:
                break;
            }

            const actions = [
                { name: 'Configuration', icon: 'cog', onClick: () => this.openConfig(false) },
                { name: 'Complex Conditions', icon: 'question-sign', onClick: this.openConditions },
                { name: 'Basic Conditions', icon: 'check', onClick: this.openBasicConditions },
                { name: 'Preview', icon: 'eye-open', onClick: PageActions.togglePreview.bind(null, pageId) },
                { name: 'Edit Metadata', icon: 'console', onClick: this.toggleMetadata },
                { name: 'Make a copy of this page', icon: 'duplicate', onClick: this.openClonePage },
                { name: 'Save', icon: 'floppy-disk', onClick: this.savePage },
            ];

            return (
                <div className={classes.join(' ')}>
                    <PageEditorCanvas
                        modelId={this.props.id}
                        pageId={pageId}
                        containers={this.props.page.model.pageContainers.data}
                        components={this.props.page.model.pageComponents.data}
                        flowKey={flowKey}
                    />
                    <Actionbar actions={actions} container={this} />
                    <CSSTransitionGroup
                        transitionName="page-config"
                        transitionEnter
                        transitionLeave
                        transitionEnterTimeout={300}
                        transitionLeaveTimeout={300}
                    >
                        {configElement}
                    </CSSTransitionGroup>
                    <MetadataEditor
                        container={this}
                        isVisible={this.state.isMetadataVisible}
                        toggleMetadata={this.toggleMetadata}
                        id={model.objectData[0].externalId}
                        modelId={this.props.id}
                        flowKey={this.props.flowKey}
                        elementType="page"
                        tenantId={this.props.tenantId}
                        authenticationToken={this.props.authenticationToken}
                    />
                </div>
            );
        }
        return null;
    }

}

export default connect(
    ({ currentUser: { authenticationToken, tenantId } }) => ({ authenticationToken, tenantId }),
)(DragContext(PageEditor));
